/**
 * Controles del cuestionario.
 *
 * Son deliberadamente sobrios: quien contesta esto está tomando una decisión
 * incómoda sobre su carrera y va a pasar cuarenta minutos aquí. La interfaz
 * tiene que desaparecer.
 */

import { useRef, useState, type ReactNode } from "react";

import type { Option, ScaleOption } from "../../lib/sessions";

/**
 * Lo tecleado manda mientras se teclea; el servidor manda el resto del tiempo.
 *
 * Los campos se pintan con lo que hay guardado en el servidor, y el guardado
 * está amortiguado 800 ms. Sin esto, cada pulsación repintaba el campo con el
 * valor de antes y lo escrito desaparecía: el cuestionario tenía casillas y
 * escalas usables y ni un solo campo de texto que funcionara.
 *
 * La regla es el foco y no una comparación de valores. Mientras el cursor está
 * dentro, lo que se ve es lo que la persona ha escrito — aunque llegue una
 * respuesta del servidor con un valor anterior, que es justo lo que pasa cuando
 * se escribe rápido y dos guardados se cruzan. Al salir del campo se vuelve a
 * aceptar lo de fuera, que para entonces ya es lo mismo.
 */
function useBorrador<T>(externo: T) {
  const [borrador, fijar] = useState<T>(externo);
  const editando = useRef(false);
  const ultimoExterno = useRef<T>(externo);

  // Estado derivado de props, en render y no en un efecto: con un efecto el
  // campo llegaría a pintarse una vez con el valor viejo.
  if (!Object.is(externo, ultimoExterno.current)) {
    ultimoExterno.current = externo;
    if (!editando.current) fijar(externo);
  }

  return {
    valor: borrador,
    teclear: fijar,
    foco: {
      onFocus: () => {
        editando.current = true;
      },
      onBlur: () => {
        editando.current = false;
      },
    },
  };
}

export function Campo({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      {/* La etiqueta va en mayúsculas con tracking, como en el formulario del
          sitio: «Mayúsculas solo en etiquetas con tracking». La pista debajo se
          queda en caja de frase — es una frase, no una etiqueta. */}
      <span className="block text-kicker font-medium uppercase text-stone">{label}</span>
      {hint ? <span className="mt-1.5 block text-caption text-stone">{hint}</span> : null}
      <div className="mt-2">{children}</div>
    </label>
  );
}

/**
 * El campo de texto, con la forma que tiene en critero.es.
 *
 * Sin redondeo: el sistema de diseño declara `--radius-btn: 0` y sus inputs lo
 * usan. El redondeo de 8 px que había aquí venía del diseño anterior y hacía
 * que el cuestionario no se pareciera al formulario por el que se llega.
 *
 * El foco es teal, no oro: el manual reparte los dos acentos y le da a teal
 * «hover, foco y énfasis». Es el mismo tratamiento que el login, para que el
 * salto entre las tres pantallas no se note.
 */
const entrada =
  "w-full rounded-none border border-line bg-white px-3.5 py-3 text-ink " +
  "transition-colors duration-300 placeholder:text-stone-2 " +
  "focus:border-ink focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-teal";

/**
 * Una opción que se pulsa. Radio 0, porque es un botón.
 *
 * «Radio 0 en botones y CTAs: es la señal de marca más fuerte del sistema.» Lo
 * redondo del manual son los chips, las pistas de puntuación y los avatares —
 * cosas que se leen, no que se pulsan.
 */
const opcion = (activa: boolean, bloqueada = false) =>
  "rounded-none border transition-colors duration-300 focus-visible:outline " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal " +
  (bloqueada
    ? "cursor-not-allowed border-line bg-white text-stone-2"
    : activa
      ? "border-ink bg-ink text-paper"
      : "border-line bg-white text-ink hover:border-teal hover:text-teal");

export function Texto({
  value,
  onChange,
  placeholder,
  multiline,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
}) {
  const { valor, teclear, foco } = useBorrador(value);

  const escribir = (v: string) => {
    teclear(v);
    onChange(v);
  };

  if (multiline) {
    return (
      <textarea
        className={`${entrada} min-h-24 resize-y`}
        value={valor}
        placeholder={placeholder}
        onChange={(e) => escribir(e.target.value)}
        {...foco}
      />
    );
  }
  return (
    <input
      type="text"
      className={entrada}
      value={valor}
      placeholder={placeholder}
      onChange={(e) => escribir(e.target.value)}
      {...foco}
    />
  );
}

export function Numero({
  value,
  onChange,
  min,
  max,
  suffix,
}: {
  value: number | string;
  onChange: (v: number | string) => void;
  min?: number;
  max?: number;
  suffix?: string;
}) {
  const { valor, teclear, foco } = useBorrador(value);

  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        className={`${entrada} max-w-36`}
        value={valor}
        min={min}
        max={max}
        // Se conserva la cadena vacía en vez de convertirla a cero: el motor
        // distingue "no lo ha dicho" de "ha dicho cero", y en el colchón
        // económico esa diferencia cambia la penalización de viabilidad.
        onChange={(e) => {
          const v = e.target.value === "" ? "" : Number(e.target.value);
          teclear(v);
          onChange(v);
        }}
        {...foco}
      />
      {suffix ? <span className="text-sm text-ink/60">{suffix}</span> : null}
    </div>
  );
}

export function Seleccion({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: Option[];
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const activa = value === o.key;
        return (
          <button
            key={o.key}
            type="button"
            aria-pressed={activa}
            onClick={() => onChange(o.key)}
            className={"px-3.5 py-2.5 text-sm " + opcion(activa)}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function SeleccionMultiple({
  value,
  onChange,
  options,
  max,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  options: Option[];
  max?: number;
}) {
  const alternar = (key: string) => {
    if (value.includes(key)) {
      onChange(value.filter((k) => k !== key));
    } else if (!max || value.length < max) {
      onChange([...value, key]);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const activa = value.includes(o.key);
        const bloqueada = !activa && Boolean(max) && value.length >= (max as number);
        return (
          <button
            key={o.key}
            type="button"
            aria-pressed={activa}
            disabled={bloqueada}
            onClick={() => alternar(o.key)}
            className={"px-3.5 py-2.5 text-sm " + opcion(activa, bloqueada)}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function Casilla({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  children: ReactNode;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-panel border border-line bg-white p-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 accent-teal"
      />
      <span className="text-sm text-ink">{children}</span>
    </label>
  );
}

/**
 * Escala de acuerdo, el control más repetido del cuestionario: aparece 110
 * veces en los instrumentos, 18 en la rueda y 5 en el valor movilizable.
 *
 * Las opciones vienen del servidor con su etiqueta; no se numeran a mano.
 */
export function Escala({
  value,
  onChange,
  options,
  compact,
}: {
  value: number | null;
  onChange: (v: number) => void;
  options: ScaleOption[];
  compact?: boolean;
}) {
  return (
    <div className={compact ? "flex gap-1" : "flex flex-wrap gap-2"} role="radiogroup">
      {options.map((o) => {
        const activa = value === o.v;
        return (
          <button
            key={o.v}
            type="button"
            role="radio"
            aria-checked={activa}
            aria-label={o.l}
            title={o.l}
            onClick={() => onChange(o.v)}
            className={
              "text-sm " +
              (compact ? "h-10 w-10 shrink-0 tabular-nums font-medium " : "px-3.5 py-2.5 ") +
              opcion(activa)
            }
          >
            {compact ? o.v : o.l}
          </button>
        );
      })}
    </div>
  );
}

export function Deslizador({
  value,
  onChange,
  min = 0,
  max = 10,
  leyendaMin,
  leyendaMax,
}: {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  leyendaMin?: string;
  leyendaMax?: string;
}) {
  return (
    <div>
      <div className="flex items-center gap-3">
        <input
          type="range"
          min={min}
          max={max}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="h-2 w-full cursor-pointer appearance-none rounded-full bg-paper-2 accent-teal"
        />
        <span className="w-8 shrink-0 text-right font-medium tabular-nums text-ink">
          {value}
        </span>
      </div>
      {leyendaMin || leyendaMax ? (
        <div className="mt-1 flex justify-between text-xs text-ink/50">
          <span>{leyendaMin}</span>
          <span>{leyendaMax}</span>
        </div>
      ) : null}
    </div>
  );
}
